import { ApiMethod, JSONObject } from '../../../../types/types';
import { FormApiMethodsRequestData } from '../../../../api/types/types';
import { fetchApiMethod } from '../../../../api/utils/fetchApiMethod';

type HandleApiError = (error: unknown) => void;

/**
 * Builds the `$apiLookup(funcId)` JSONata binding for reference filter templates.
 * Uses `fetchApiMethod` — same fetch path as form handlers. The value is returned
 * as it comes out of the response template, so primitives and arrays are preserved.
 * Cache is per-resolve; errors resolve to undefined (no re-throw — JSONata would
 * surface it as an expression error instead of a catchable filter error).
 */
export type ApiLookupFunction = (funcId: unknown) => Promise<unknown>;

export type ApiLookupParams = {
  apiLookup: ApiLookupFunction;
  // Caller checks this AFTER resolveTemplate to decide whether to abort
  // the dataset fetch and surface a load error in the UI.
  hasError: () => boolean;
};

export const createApiLookupFunction = (
  apiMethods: Record<string, ApiMethod>,
  context: FormApiMethodsRequestData & JSONObject,
  // handleApiError from useApiErrorHandling
  handleApiError: HandleApiError
): ApiLookupParams => {
  const cache = new Map<string, Promise<unknown>>();
  // Sticky flag — once any $apiLookup call has failed, the whole filter
  // resolution is considered tainted.
  let errored = false;

  const apiLookup: ApiLookupFunction = async (funcId) => {
    if (typeof funcId !== 'string' || !funcId) {
      throw new Error('$apiLookup requires a string funcId argument');
    }

    const apiMethod = apiMethods[funcId];
    if (!apiMethod) {
      throw new Error(
        `$apiLookup: "${funcId}" is not defined in form.apiMethods`
      );
    }

    let pending = cache.get(funcId);
    if (!pending) {
      // Wrap in an inner async so the promise stored in cache resolves to
      // undefined on failure (instead of rejecting and tripping JSONata).
      pending = (async () => {
        try {
          return await fetchApiMethod(apiMethod, context);
        } catch (error) {
          errored = true;
          handleApiError(error);
          return undefined;
        }
      })();
      cache.set(funcId, pending);
    }
    return await pending;
  };

  return {
    apiLookup,
    hasError: () => errored,
  };
};
